import MapboxDraw, { constants, DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import { Point } from "mapbox-gl";

import readOnlySelect from "../base/readOnlySelect";

import { NBN_SOURCE, NBN_SOURCE_LAYER, NBN_SOURCE_LAYER_FILTER } from "./constants";

const {
  lib: {
    CommonSelectors: { isFeature, noTarget, isOfMetaType },
  },
} = MapboxDraw;

/**
 * Returns a Point representing a mouse event's position
 * relative to a containing element.
 *
 * @param {MouseEvent} mouseEvent
 * @param {Node} container
 * @returns {Point}
 */
function mouseEventPoint(mouseEvent: any, container: any) {
  const rect = container.getBoundingClientRect();
  return new Point(mouseEvent.clientX - rect.left - (container.clientLeft || 0), mouseEvent.clientY - rect.top - (container.clientTop || 0));
}

/**
 * This is a custom draw mode that allows for the user to select existing draw features on the map
 * or to select features from the NBN "input_layers/base_data/sl" layer.
 *
 * Selection of NBN layer features is done either by clicking on the feature or by box selecting.
 *
 * Selected features cannot be moved in this mode.
 */
const nbnSelect: DrawCustomMode = {
  ...readOnlySelect,

  /**
   * This is a custom onClick function to handle the creation of drawFeatures when clicking on a feature that
   * is sourced from a vector layer (i.e. not an existing draw feature)
   */
  onClick: function (state, event) {
    // Click (with or without shift) on no feature
    if (noTarget(event)) {
      const vectorFeatures = this.getFeatures(event);

      if (vectorFeatures.length > 0) {
        const drawFeature = this.addDrawFeature(vectorFeatures[0]);
        const newEvent = { ...event, featureTarget: drawFeature } as MapMouseEvent;
        return this.clickOnFeature(state, newEvent);
      }

      return this.clickAnywhere(state, event); // also tap
    }
    if (isOfMetaType(constants.meta.VERTEX)(event)) return this.clickOnVertex(state, event); // tap
    if (isFeature(event)) return this.clickOnFeature(state, event);

    return undefined;
  },

  /**
   * A custom onMouseUp to handle the creation of drawFeatures when box selecting
   */
  onMouseUp: function (state, event) {
    // End any extended interactions
    if (state.dragMoving) {
      this.fireUpdate();
    } else if (state.boxSelecting) {
      // Check if the user has included within the box selection any features
      // within the "input_layers/base_data/sl" layer & if so create those draw features
      // and add them to the drawControl
      const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        state.boxSelectStartLocation,
        mouseEventPoint(event.originalEvent, this.map.getContainer()),
      ];
      const vectorFeatures = this.getFeatures(event, bbox);
      vectorFeatures.forEach((feature) => {
        this.addDrawFeature(feature);
      });

      const drawFeatures = this.featuresAt(null, bbox, "click");
      const featuresInBox = [...vectorFeatures, ...drawFeatures];
      const idsToSelect = this.getUniqueIds(featuresInBox).filter((id) => !this.isSelected(id));

      if (idsToSelect.length) {
        this.select(idsToSelect);
        idsToSelect.forEach((id) => this.doRender(id));
        this.updateUIClasses({ mouse: constants.cursors.MOVE });
      }
    }
    this.stopExtendedInteractions(state);
  },

  /**
   * Gets the features from the map that are not draw features.
   *
   * NOTE: Since this is getting the features using the queryRenderedFeatures method,
   * it will return feature geometry used by the map for rendering. This means that at
   * high zoom levels, the precision of the geometry may be lower than the original which
   * results in the features returned potentially not being the same as the original.
   */
  getFeatures: function (
    this: MapboxDraw.DrawCustomModeThis,
    event: MapboxDraw.MapMouseEvent,
    bbox?: [mapboxgl.PointLike, mapboxgl.PointLike]
  ): mapboxgl.MapboxGeoJSONFeature[] {
    // Check if there is a vector feature we need to create
    const queryBbox: [mapboxgl.PointLike, mapboxgl.PointLike] = bbox || [
      [event.point.x - 5, event.point.y - 5],
      [event.point.x + 5, event.point.y + 5],
    ];

    return this.map
      ?.queryRenderedFeatures(queryBbox, {
        filter: NBN_SOURCE_LAYER_FILTER,
      })
      .filter(
        (feature: mapboxgl.MapboxGeoJSONFeature) =>
          feature.source === NBN_SOURCE && feature.sourceLayer === NBN_SOURCE_LAYER && !feature.state.isEditing
      );
  },

  /**
   * Adds a draw feature to the drawControl and sets the feature state to isEditing for the underlying
   * vector feature (which styles use to hide the feature behind the draw feature)
   */
  addDrawFeature(this: MapboxDraw.DrawCustomModeThis, feature: mapboxgl.MapboxGeoJSONFeature): MapboxDraw.DrawFeature {
    const drawFeature = this.newFeature(feature);
    const featureId = feature.properties?.id;
    this.map?.setFeatureState({ id: featureId, source: NBN_SOURCE, sourceLayer: NBN_SOURCE_LAYER }, { isEditing: true });
    this.addFeature(drawFeature);

    return drawFeature;
  },
};

export default nbnSelect;
